import type { VNode } from 'preact';

/**
 * Micro-renderer markdown pour les messages de l'agent.
 *
 * Pourquoi pas une lib (marked, markdown-it) : poids (le bundle vise < 30 KB
 * gzipped) et surtout sécurité. On ne génère JAMAIS de HTML via
 * dangerouslySetInnerHTML — chaque token devient un VNode Preact, donc le
 * texte de l'agent est toujours échappé par le moteur de rendu. Pas de surface
 * XSS, même si le LLM crache du HTML ou un `javascript:` dans un lien (les
 * liens sont restreints à http/https par la regex).
 *
 * Couverture volontairement limitée à ce qu'un agent conversationnel produit :
 *   - gras **texte** / __texte__
 *   - italique *texte* / _texte_
 *   - code inline `texte`
 *   - liens [libellé](https://...)
 *   - titres # / ## / ... (rendus en gras, pas en h1 géant dans une bulle)
 *   - listes à puces (- ou *) et numérotées (1.)
 *   - paragraphes (ligne vide) et sauts de ligne simples (<br>)
 *
 * Le formatage imbriqué (gras dans une puce) marche ; l'imbrication profonde
 * (gras DANS de l'italique) n'est pas gérée, cas marginal pour du chat. Pendant
 * le streaming, un marqueur non encore fermé (`**bold` sans `**`) reste affiché
 * en texte brut jusqu'à l'arrivée de sa fermeture : flicker léger, pas de crash.
 */

// Ordre des alternatives important : ** avant *, __ avant _, pour matcher le
// marqueur long en priorité. Tous non-greedy pour s'arrêter au plus tôt.
const INLINE_RE =
  /\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*|_(.+?)_|`(.+?)`|\[(.+?)\]\((https?:\/\/[^\s)]+)\)/;

function parseInline(text: string): Array<VNode | string> {
  const out: Array<VNode | string> = [];
  let rest = text;
  let i = 0;

  while (rest.length > 0) {
    const m = INLINE_RE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));

    const key = `i${i++}`;
    if (m[1] !== undefined) out.push(<strong key={key}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<strong key={key}>{m[2]}</strong>);
    else if (m[3] !== undefined) out.push(<em key={key}>{m[3]}</em>);
    else if (m[4] !== undefined) out.push(<em key={key}>{m[4]}</em>);
    else if (m[5] !== undefined) out.push(<code key={key}>{m[5]}</code>);
    else if (m[6] !== undefined && m[7] !== undefined) {
      out.push(
        <a key={key} href={m[7]} target="_blank" rel="noopener noreferrer">
          {m[6]}
        </a>,
      );
    }

    rest = rest.slice(m.index + m[0].length);
  }

  return out;
}

const HEADING_RE = /^#{1,6}\s+(.*)$/;
const BULLET_RE = /^[-*]\s+(.*)$/;
const NUMBERED_RE = /^\d+\.\s+(.*)$/;

export function Markdown({ text }: { text: string }): VNode {
  const lines = text.split('\n');
  const blocks: VNode[] = [];
  let para: string[] = [];
  let listItems: VNode[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let k = 0;

  const flushPara = () => {
    if (para.length === 0) return;
    const content: Array<VNode | string> = [];
    let first = true;
    for (const line of para) {
      // Saut de ligne simple entre deux lignes d'un même paragraphe. Key
      // dérivée du compteur global k (pas de l'index) pour éviter le pattern
      // index-as-key et garder des clés stables.
      if (!first) content.push(<br key={`br${k++}`} />);
      first = false;
      for (const node of parseInline(line)) content.push(node);
    }
    blocks.push(
      <p key={`b${k++}`} className="sfa-md-p">
        {content}
      </p>,
    );
    para = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const items = listItems;
    blocks.push(
      listType === 'ol' ? (
        <ol key={`b${k++}`} className="sfa-md-list">
          {items}
        </ol>
      ) : (
        <ul key={`b${k++}`} className="sfa-md-list">
          {items}
        </ul>
      ),
    );
    listItems = [];
    listType = null;
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (line === '') {
      flushPara();
      flushList();
      continue;
    }

    const heading = HEADING_RE.exec(line);
    const bullet = BULLET_RE.exec(line);
    const numbered = NUMBERED_RE.exec(line);

    if (heading) {
      flushPara();
      flushList();
      blocks.push(
        <p key={`b${k++}`} className="sfa-md-h">
          {parseInline(heading[1] ?? '')}
        </p>,
      );
    } else if (bullet) {
      flushPara();
      if (listType && listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(<li key={`li${k++}`}>{parseInline(bullet[1] ?? '')}</li>);
    } else if (numbered) {
      flushPara();
      if (listType && listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(<li key={`li${k++}`}>{parseInline(numbered[1] ?? '')}</li>);
    } else {
      flushList();
      para.push(line);
    }
  }

  flushPara();
  flushList();

  return <div className="sfa-md">{blocks}</div>;
}
